import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { BidderClientFlagsSchema, type BidderClientFlags } from '@neon-one/auctions-types';

import { httpClient } from '@/shell/http';
import { useSession } from '@/shell/stores/session';

// How long to wait before the anonymous convergence refetch (see reload()).
// Must exceed the backend's FEATURE_FLAGS_CACHE_TTL_MS
// (auctions-backend/src/config/index.ts — default 15_000, currently overridden
// nowhere). Raising that env var invalidates this constant.
export const FLAG_REFRESH_DELAY_MS = 16_000;

// Evaluated, org-scoped bidder-facing feature flags for the org being browsed
// (GET /public/feature-flags). Every gated boolean DEFAULTS TO `false`
// until loaded and on any error, preserving hidden-by-default behavior — a
// flag-fetch failure must never break the bidder UI or reveal a dark feature.
//
// The bidder allow-list (BIDDER_CLIENT_FLAG_KEYS) is EMPTY today, so the
// endpoint returns `{}` and there are no convenience computeds to expose yet.
// This store is the complete listen-for-change -> refetch loop so the moment a
// bidder-facing flag key lands in @neon-one/auctions-types, only a computed
// (see below) needs to be added here — the fetch + WS wiring already work.
//
// load() is idempotent (skips when already loaded/loading) and swallows all
// errors. reload() force-refetches (bypassing the loaded guard) on a
// feature_flag.changed WS frame or a WS reconnect. A non-reactive version
// token guards against a slow response landing after a newer reload was
// issued — the stale response is discarded rather than clobbering fresh state.
export const useFeatureFlagsStore = defineStore('featureFlags', () => {
  const flags = ref<BidderClientFlags | null>(null);
  const loaded = ref(false);
  const loading = ref(false);
  // Not reactive — a plain closure counter bumped on every fetch so an
  // in-flight request that resolves after a newer one can't write its result.
  let flagsToken = 0;
  // Closure-scoped (not module-scoped) so it does not survive a new Pinia
  // instance and leak a pending timer across tests or parcel remounts.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  // IMPORTANT: the session is read ONLY to decide whether to schedule the
  // delayed convergence refetch below. The flag FETCH ITSELF IS NEVER GATED ON
  // AUTH — anonymous visitors must receive flags, because bidder flags gate
  // features visible to non-authenticated bidders and anonymous browsing is the
  // primary bidder funnel (GT1-492). Adding something like
  // `if (!session.isAuthenticated) return` to load()/reload()/fetchFlags()
  // re-breaks anonymous flag evaluation for the majority of traffic. The
  // anonymous-path specs in tests/shell/featureFlags.spec.ts exist to fail if
  // anyone does.
  //
  // getJwt() rather than isAuthenticated: it returns null for `mock-jwt-*`
  // prototype tokens, which are never sent over the wire, so it matches what
  // the request will actually carry (see shell/http/index.ts). One consequence
  // in demo mode: a mock session counts as anonymous here and so still gets a
  // convergence refetch — harmless, just one extra GET.
  const session = useSession();

  // Shared fetch/parse/assign used by both load() and reload(). Owns the
  // loading flag lifecycle and swallows all errors defensively.
  async function fetchFlags(fresh = false): Promise<void> {
    const token = ++flagsToken;
    loading.value = true;
    try {
      // `?fresh=1` tells the backend to bypass its 15s per-container flag cache
      // for this read. Used only by reload() (WS-driven / reconnect), so a
      // scheduled/live flip reflects immediately instead of waiting out the TTL;
      // the idempotent initial load() stays on the cached path.
      const url = fresh ? '/public/feature-flags?fresh=1' : '/public/feature-flags';
      const resp = await httpClient().get<BidderClientFlags>(url);
      // A newer fetch was issued while this one was in flight — discard.
      if (token !== flagsToken) return;
      const parsed = BidderClientFlagsSchema.safeParse(resp.data);
      if (parsed.success) {
        flags.value = parsed.data;
        loaded.value = true;
      } else {
        // Malformed payload — leave safe (hidden-by-default) state; a later
        // reload retries. Never throw out of the flag loop.
        console.error('[featureFlags] invalid feature-flag payload', parsed.error);
      }
    } catch (e) {
      if (token !== flagsToken) return;
      // Never throw: a flag fetch failure must not break the bidder UI.
      console.error('[featureFlags] failed to load feature flags', e);
    } finally {
      if (token === flagsToken) loading.value = false;
    }
  }

  // Idempotent initial load — no-op once loaded or while a load is in flight.
  async function load(): Promise<void> {
    if (loaded.value || loading.value) return;
    await fetchFlags();
  }

  // Cancels a pending convergence refetch. Idempotent.
  function cancelPendingRefresh(): void {
    if (refreshTimer !== null) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  // Force a refetch bypassing the loaded guard. Called on a
  // feature_flag.changed WS frame and on WS reconnect (a reconnect may have
  // missed a broadcast).
  async function reload(): Promise<void> {
    await fetchFlags(true);

    // The backend honours `?fresh=1` only for authenticated callers, so an
    // anonymous reload just read this container's cached snapshot and may lag
    // the flip we were told about by up to one TTL. Schedule ONE plain refetch
    // past that TTL to converge.
    //
    // Waiting FLAG_REFRESH_DELAY_MS is sufficient, not merely best-effort:
    // snapshot age only grows, so by then any serving container's snapshot was
    // either loaded after the flip (already fresh) or is older than the TTL and
    // gets reloaded on this request. Multi-container concurrency doesn't break
    // that, so this does not need to become a polling loop.
    if (session.getJwt() !== null) return;
    // Never stack: a feature_flag.changed frame and a reconnect can both fire.
    cancelPendingRefresh();
    refreshTimer = setTimeout(() => {
      // Cleared before firing so a later cancel is a no-op rather than a
      // clearTimeout on an already-elapsed handle.
      refreshTimer = null;
      void fetchFlags();
    }, FLAG_REFRESH_DELAY_MS);
  }

  // A JWT appearing makes any pending anonymous convergence refetch moot — the
  // next reload() carries the token and gets `?fresh=1` honoured directly.
  // Lives here rather than in App.vue's auth watcher so it is covered by the
  // store specs; no bidder-web spec mounts App.vue.
  watch(
    () => session.getJwt(),
    (jwt) => {
      if (jwt !== null) cancelPendingRefresh();
    },
  );

  // Future bidder-facing flags expose a boolean computed here, e.g.:
  //   const someBidderFlag = computed(() => flags.value?.['bidder.some-flag'] ?? false);
  // and add it to the return below. Allow-list is empty today — none yet.

  return { flags, loaded, loading, load, reload, cancelPendingRefresh };
});
